import { action, observable, computed } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { AppManager, Input, routerStore } from 'core';
import { emailValidator } from 'libs/forms/validators';

import { CompleteProfilesQuery } from 'apps/profile/gql';
import { PaymentMethodsAppManager } from 'apps/payment/method/appManager';
import { clientStore } from 'apps/client/app';
import {
  getInviteLink,
  redirectToInviteLink,
  raiseUserError,
} from 'apps/utils';
import { AddPaymentMethodMutation } from 'apps/payment/gql';
import { addWeeks } from 'date-fns';
import { formatDate } from 'utils/formatStrings';
import { DefaultMenuRoute } from 'constants/global';
import { isInitiativeSpace } from 'apps/routes/utils';
import { Project, User } from 'generated/typescript';
import { isMobile } from 'styles/utils';
import {
  CreateSubscriptionMutation,
  InitiativeQuery,
  MembersByProjects,
  ProfileTransferProjectsQuery,
  UpdateSubscriptionMutation,
} from './gql';

export declare type TeamMember = {
  username?: string;
  email?: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  isPendingInvited?: boolean;
  isFromProject?: boolean;
};

export type CreditCard = {
  id: string;
  brand: string;
  country?: string;
  expMonth: number;
  expYear: number;
  lastDigits: string;
  isDefault: boolean;
  name?: string;
};

export class SubscriptionManager extends PaymentMethodsAppManager {
  @observable activeKey: string | number;

  @observable teamName: string;

  @observable addCardFormVisible: boolean = false;

  @observable taxId: string;
  @observable countryCode: string;

  @observable stripeToken: string;

  @observable billingCycle: 'yearly' | 'monthly' = 'monthly';

  @observable additionalSeats: number = 0;

  @observable includedPlanSeats: number = 0;

  @observable teamMembers: TeamMember[] = [];

  @observable productType: 'pro' | 'business';

  @observable saveUpTo: number = 0.8;

  @observable submitting = false;

  @observable profiles: any[];

  @observable subscriptionConfirmed = false;

  @observable isStartFreeTrial = true;

  @observable currentPeriodEnd: Date;

  @observable initiativeQuery: InitiativeQuery;

  @observable queryMembersByProjects: MembersByProjects;

  @observable transferProjectsQuery: ProfileTransferProjectsQuery;

  @observable componentMode: 'edit' | 'readOnly' = 'edit';

  @observable creditCards: CreditCard[] = [];

  @observable earlyUpgrade: boolean = false;

  @observable cardId: string;

  @observable projects: Project[] = [];

  @observable selectedProjects: string[] = [];

  @observable outsideCollaborators: number = 0;

  operateSubscription: Record<string, any>;

  addPaymentSuccessCallback: () => void;

  @action setBillingCycle = (billingCycle: 'yearly' | 'monthly') => {
    this.billingCycle = billingCycle;
  };

  constructor(parentAppManager: AppManager, init = {}) {
    super(parentAppManager, init);
  }

  @action GetCurrentSpaceId = () => {
    const initiativeSlug = routerStore.route.params.spacePath.replace('+', '');
    const currentInitiative = clientStore.workspaces.find(
      workspace =>
        workspace.content.slug === initiativeSlug &&
        workspace.spaceType === 'INITIATIVE',
    );

    return currentInitiative?.id;
  };

  @action SetBillingCycle = (billingCycle: 'yearly' | 'monthly') => {
    this.billingCycle = billingCycle;
  };

  @action SetProductType = (productType: 'pro' | 'business') => {
    this.productType = productType;
  };

  @action SetStartFreeTrial = (FreeTrial: boolean) => {
    this.isStartFreeTrial = FreeTrial;
  };

  @action SetCurrentPeriodEnd = (currentPeriodEnd: Date) => {
    this.currentPeriodEnd = currentPeriodEnd;
  };

  @action SetEarlyUpgrade = (earlyUpgrade: boolean) => {
    this.earlyUpgrade = earlyUpgrade;
  };

  @action SetAdditionalSeats = (additionalSeats: number) => {
    this.additionalSeats = additionalSeats;
  };

  @action SetIncludedPlanSeats = (includedPlanSeats: number) => {
    this.includedPlanSeats = includedPlanSeats;
  };

  @action SetTeamMembers = (teamMembers: TeamMember[]) => {
    this.teamMembers = teamMembers;
  };

  @action RemoveTeamMember = (value: string) => {
    const type = emailValidator(value) ? 'email' : 'username';

    this.teamMembers = this.teamMembers.filter(m => m[type] !== value);
  };

  @computed get loading() {
    return this.initiativeQuery?.loading || false;
  }

  @computed get productPrice() {
    if (this.productType === 'pro') {
      return 25;
    }

    if (this.productType === 'business') {
      return 55;
    }

    return 0;
  }

  getCouponPrice = (productType: 'pro' | 'business') => {
    if (productType === 'pro') {
      return 20;
    }
    if (productType === 'business') {
      return 45;
    }

    return 0;
  };

  @computed get additionalPrice() {
    if (this.billingCycle === 'yearly') {
      return this.additionalSeats * this.getCouponPrice(this.productType);
    }
    return this.additionalSeats * this.productPrice;
  }

  @computed get billingMonths() {
    if (this.billingCycle === 'yearly') {
      return 12;
    }

    return 1;
  }

  @action formatNextPaymentDate = (formatter: string = 'MMM do') => {
    if (this.isStartFreeTrial) {
      return formatDate(addWeeks(new Date(), 2), formatter);
    }
    return formatDate(this.currentPeriodEnd, formatter);
  };

  // invited editors + additional editors
  @computed get seats() {
    if (this.earlyUpgrade) {
      return (
        this.includedPlanSeats +
        this.editorsNumber +
        this.adminsNumber +
        this.ownersNumber +
        this.outsideCollaborators
      );
    }

    return (
      this.includedPlanSeats +
      this.additionalSeats +
      this.editorsNumber +
      this.adminsNumber +
      this.ownersNumber
    );
  }

  calculateRolesNumber = (
    teamMembers: TeamMember[],
    role: 'owner' | 'admin' | 'editor' | 'viewer',
  ) => {
    return teamMembers.filter(member => member.role === role).length;
  };

  @computed get viewersNumber() {
    return this.calculateRolesNumber(this.teamMembers, 'viewer');
  }

  @computed get editorsNumber() {
    return this.calculateRolesNumber(this.teamMembers, 'editor');
  }

  @computed get adminsNumber() {
    return this.calculateRolesNumber(this.teamMembers, 'admin');
  }

  @computed get ownersNumber() {
    return this.calculateRolesNumber(this.teamMembers, 'owner');
  }

  @computed get annualPriceDetails() {
    const seats = Math.abs(this.seats - this.includedPlanSeats);
    const productPriceByDiscount = this.getCouponPrice(this.productType);
    const originPrice = this.productPrice * this.billingMonths * seats;
    let coupon = 0.8;

    if (this.productType === 'pro') {
      coupon = 20;
    }

    if (this.productType === 'business') {
      coupon = 18;
    }

    return {
      unsavedPercentage: coupon,
      productPriceByDiscount,
      originPrice,
    };
  }

  @computed get totalEditorsPrice() {
    const seats = Math.abs(this.seats - this.includedPlanSeats);

    if (this.billingCycle === 'yearly') {
      return this.getCouponPrice(this.productType) * 12 * seats;
    }

    return this.productPrice * seats;
  }

  @computed get totalIncludedEditorsPrice() {
    if (this.billingCycle === 'yearly') {
      return (
        12 * this.includedPlanSeats * this.getCouponPrice(this.productType)
      );
    }

    return this.productPrice * this.includedPlanSeats;
  }

  @computed get totalPrice() {
    const seats = Math.abs(this.seats - this.includedPlanSeats);
    if (this.includedPlanSeats > this.seats) {
      return 0;
    }

    if (this.billingCycle === 'yearly') {
      return 12 * seats * this.getCouponPrice(this.productType);
    }

    return this.productPrice * seats;
  }

  @action SubscriptionSuccess = async (result: Record<string, any>) => {
    this.SetAddCardFormVisible(false);

    const { space } = result.subscription;
    const { content, ...inSpace } = space;
    const isInitiative = isInitiativeSpace(space);

    const spaceData = {
      inSpace,
      content,
    };

    if (isInitiative) {
      await clientStore.UpdateTeamSpace(spaceData);
    } else {
      await clientStore.UpdateProfileSpace(spaceData);
    }

    this.operateSubscription = result.subscription;
    this.subscriptionConfirmed = true;
    clientStore.SetActiveWorkspace(result.subscription.space.id);
    this.submitting = false;
    this.newCard?.SetSubmitting(false);
  };

  @action GetStarted = async () => {
    if (!clientStore.profileResponse) {
      await clientStore.WhoAmI();
    }

    const currentUsername = clientStore.profileResponse.username;

    const { content, id, spaceType } = this.operateSubscription?.space || {};

    const isInitiative = spaceType === 'INITIATIVE';

    if (id) {
      clientStore.SetActiveWorkspace(id);
    }

    const dashboard = DefaultMenuRoute;

    this.operateSubscription = null;

    // if it's edit mode, we will goto dashboard
    // otherwise, we will goto billing page
    const navigateTo =
      this.componentMode === 'edit'
        ? dashboard
        : 'workspace.spaceSettings.billing';

    if (
      routerStore.route.name?.startsWith?.('onboarding.subscription') &&
      getInviteLink()
    ) {
      redirectToInviteLink();

      return;
    }

    routerStore.navigate(navigateTo, {
      spacePath: isInitiative && content ? `+${content.slug}` : currentUsername,
    });
  };

  @action CreateSubscriptionError = () => {
    Alert.error(
      i18next.t(
        'Hmm... We failed to submit your Application. Please try again.',
      ),
    );

    this.submitting = false;
    this.newCard?.SetSubmitting(false);
  };

  @action AddPaymentMethodError = () => {
    Alert.error(
      i18next.t('Your card has been declined, please try another one'),
    );

    this.newCard?.SetSubmitting(false);
  };

  @action SetAddCardFormVisible = (visible: boolean) => {
    this.addCardFormVisible = visible;
  };

  @action UpdatePlanError = (error: any) => {
    raiseUserError(error, 'update', 'plan');

    this.submitting = false;
  };

  @action StartFreeTrial = () => {
    this.submitting = true;

    let input = {};

    const hasBillingSubscription =
      clientStore.signedInProfile.billingSubscriptions.length > 0;

    const productType = this.productType === 'pro' ? 'team' : 'business';

    if (hasBillingSubscription) {
      input = new Input({
        paymentMethod: {
          cardToken: this.stripeToken,
          defaultPaymentMethod: true,
        },
        productType,
        billingCycle: this.billingCycle,
        projectIds: this.selectedProjects,
        initiative: {
          name: this.teamName,
          users: this.teamMembers
            .filter(member => member.role)
            .map(member => ({
              username: member.username,
              role: member.role,
            })),
        },
        extraSeats: this.additionalSeats,
        startFreeTrial: true,
        taxId: this.taxId,
        countryCode: this.countryCode,
      });
      // team without billing
    } else {
      input = new Input({
        productType,
        billingCycle: this.billingCycle,
        projectIds: this.selectedProjects,
        initiative: {
          name: this.teamName,
          users: this.teamMembers
            .filter(member => member.role)
            .map(member => ({
              username: member.username,
              role: member.role,
            })),
        },
        extraSeats: this.additionalSeats,
        startFreeTrial: true,
      });
    }

    const createSubscriptionMutation = new CreateSubscriptionMutation(
      input,
      null,
      {
        onSuccess: this.SubscriptionSuccess,
        onSysErr: this.CreateSubscriptionError,
        onUserErr: this.CreateSubscriptionError,
      },
    );

    createSubscriptionMutation.mutate();
  };

  @action SetActiveKey = (activeKey: string) => {
    this.activeKey = activeKey;
  };

  @action OnTaxIdChange = (taxId: string) => {
    this.taxId = taxId;
  };

  @action OnCountryChange = (countryCode: string) => {
    this.countryCode = countryCode;
  };

  @action SetTeamName = (teamName: string) => {
    this.teamName = teamName;
  };

  @action OnAddSeat = () => {
    this.activeKey = 'addCollaborators';
  };

  @action OnTransferProjectsNext = () => {
    this.QueryMembersByProjectIds();
  };

  @action QueryMembersByProjectIds = () => {
    this.queryMembersByProjects = new MembersByProjects(this, {
      variables: {
        spaceId: clientStore.signedInProfile.inSpace.id,
        projectIds: this.selectedProjects,
      },
    });

    this.queryMembersByProjects.process(
      // identity here because the processor modifies the data
      // so we need to do it in an mobx untracked way, which the
      // second processor is by default. So we just forward to it.
      (x: any) => x,
      (data: any) => {
        if (!data) return;
        this.teamMembers = this.teamMembers.filter(
          member => !member.isFromProject,
        );
        if (data.length > 0) {
          this.teamMembers = [
            ...this.teamMembers,
            ...data.map((member: any) => ({
              username: member.username,
              email: member.email,
              role: 'viewer',
              isFromProject: true,
            })),
          ];
        }

        // to next one
        this.activeKey = 'addCollaborators';
      },
    );

    this.queryMembersByProjects.query();
  };

  @action OnCreateTeamNext = () => {
    if (!this.teamName) {
      Alert.error(i18next.t('Team Name is required.'));
      return;
    }

    if (
      routerStore.route.name === 'home.newInitiative' &&
      !isMobile() &&
      this.projects?.length > 0
    ) {
      this.activeKey = 'transferProjects';
      return;
    }

    this.activeKey = 'addCollaborators';
  };

  @action OnCreditCardSelected = (cardId: string) => {
    this.cardId = cardId;
  };

  @action OnProjectSelected = (projectId: string) => {
    const projectIdSelected = this.selectedProjects.find(
      pid => pid === projectId,
    );

    if (projectIdSelected) {
      this.selectedProjects = this.selectedProjects.filter(
        pid => pid !== projectId,
      );
    } else {
      this.selectedProjects.push(projectId);
    }
  };

  @action QueryProfileTransferProjects = () => {
    this.transferProjectsQuery = new ProfileTransferProjectsQuery(this, {
      variables: {
        username: clientStore.signedInProfile.username,
        batchSize: 100,
      },
    });

    this.transferProjectsQuery.process((data: any) => {
      if (!data) return;
      this.projects = data.edges.map((edge: any) => edge.node);
      // default checked
      this.selectedProjects = this.projects.map(p => p.id);
    });

    this.transferProjectsQuery.query();
  };

  @action QueryProfiles = (name: string) => {
    const query = new CompleteProfilesQuery(this, {
      variables: {
        complete: name,
      },
    });

    query.process((data: any) => {
      if (!data) return;

      this.profiles = data;
    });

    query.query();
  };

  @action SwitchProductType = (productType: 'pro' | 'business') => {
    this.productType = productType;
  };

  @action OnMemberClick = (member: string) => {
    if (this.teamMembers.find(m => m.username === member)) return;

    this.teamMembers = [
      ...this.teamMembers,
      {
        username: member,
        role: 'viewer',
      },
    ];
  };

  @action OnStepClick = (stepKey: string) => {
    if (this.activeKey === 'createTeam') {
      if (!this.teamName) {
        Alert.error(i18next.t('Team Name is required.'));
        return;
      }
    }

    this.SetActiveKey(stepKey);
  };

  @action AddEmail = (email: string) => {
    this.teamMembers = [
      ...this.teamMembers,
      {
        email,
        role: 'viewer',
      },
    ];
  };

  @action AddPaymentMethod = (stripeToken: string): any => {
    this.stripeToken = stripeToken;

    const { spacePath } = routerStore.route.params;

    if (
      routerStore.route.name === 'workspace.upgrade' &&
      this.productType !== 'pro'
    ) {
      const isInitiative = routerStore.route.params.spacePath.startsWith('+');
      // update flow has spacePath
      if (isInitiative) {
        this.newCard?.SetSubmitting(true);
        const input = new Input({
          spaceSlug: () => spacePath,
          stripeToken: () => stripeToken,
        });
        this.paymentSpace = null;
        AddPaymentMethodMutation.New(input, {
          onSuccess: () => {
            this.addPaymentSuccessCallback?.();
            this.SetAddCardFormVisible(false);
            this.newCard?.SetSubmitting(false);
          },
          onSysErr: this.AddPaymentMethodError,
          onUserErr: this.AddPaymentMethodError,
        });
      }

      return null;
    }

    return null;
  };

  @action HandleGroupsAndInviteUsers = (
    data: {
      profile: { email?: string; username: string };
      email?: string;
    }[],
    role: 'owner' | 'admin' | 'editor' | 'viewer',
    isPendingInvited: boolean = false,
  ) => {
    data.forEach(
      (member: {
        profile: { email?: string; username?: string };
        email?: string;
      }) => {
        if (!member.profile && !member.email) {
          return;
        }

        // handle the email which is not in the system
        if (member.email) {
          const memberExisted = this.teamMembers.find(
            m => m.email === member.email,
          );

          if (memberExisted) {
            return;
          }

          this.teamMembers = [
            ...this.teamMembers,
            {
              email: member.email,
              role,
              isPendingInvited,
            },
          ];

          return;
        }

        const memberExisted = this.teamMembers.find(m => {
          return (
            (member.profile.username &&
              m.username === member.profile.username) ||
            (member.profile.email && m.email === member.profile.email)
          );
        });

        if (memberExisted) {
          return;
        }

        this.teamMembers = [
          ...this.teamMembers,
          {
            username: member.profile.username,
            email: member.profile.email,
            role,
            isPendingInvited,
          },
        ];
      },
    );
  };

  @action UpdatePlan = () => {
    this.submitting = true;

    const spaceId = this.GetCurrentSpaceId();

    const input = new Input({
      cardId: this.cardId,
      billingCycle: this.billingCycle,
      productType: this.productType === 'pro' ? 'team' : 'business',
      spaceId,
    });

    const updateSubscriptionMutation = new UpdateSubscriptionMutation(
      input,
      null,
      {
        onSuccess: this.SubscriptionSuccess,
        onSysErr: this.UpdatePlanError,
        onUserErr: this.UpdatePlanError,
      },
    );

    updateSubscriptionMutation.mutate();
  };

  @action SortGroupRoles = (groups: any) => {
    const sortOrder = ['owner', 'admin', 'member', 'viewer'];

    return groups?.sort((prev: any, next: any) => {
      return sortOrder.indexOf(prev.name) - sortOrder.indexOf(next.name);
    });
  };

  @action SetOutsideCollaborators = (outsideCollaborators: number) => {
    this.outsideCollaborators = outsideCollaborators;
  };

  @action HandleMembers = (groups: any) => {
    if (!groups) return;
    groups.forEach((group: any) => {
      // not handle owner, since owner is default handled in useEffect
      if (group.name === 'owner') {
        this.HandleGroupsAndInviteUsers(group.members, 'owner');
        this.HandleGroupsAndInviteUsers(group.pendingInvites, 'owner', true);
      }

      if (group.name === 'admin') {
        this.HandleGroupsAndInviteUsers(group.members, 'admin');
        this.HandleGroupsAndInviteUsers(group.pendingInvites, 'admin', true);
      }

      if (group.name === 'member') {
        this.HandleGroupsAndInviteUsers(group.members, 'editor');
        this.HandleGroupsAndInviteUsers(group.pendingInvites, 'editor', true);
      }

      if (group.name === 'viewer') {
        this.HandleGroupsAndInviteUsers(group.members, 'viewer');
        this.HandleGroupsAndInviteUsers(group.pendingInvites, 'viewer', true);
      }
    });
  };

  @action SetCreditCards = (creditCards: CreditCard[]) => {
    this.creditCards = creditCards;

    creditCards.forEach((card: CreditCard) => {
      if (card.isDefault) {
        this.cardId = card.id;
      }
    });
  };

  @action SetComponentMode = (mode: 'edit' | 'readOnly') => {
    this.componentMode = mode;
  };
}
